import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MessageSquareWarning,
  Sparkles,
  AlertCircle,
  RotateCw,
  Filter as FilterIcon,
  AlertTriangle,
  HelpCircle,
  ThumbsDown,
} from "lucide-react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { EmptyState } from "@/components/ui/EmptyState";
import { useShell } from "@/components/Shell";
import {
  listGaps,
  type GapsPayload,
  type GapReason,
  type ResolutionFilter,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { GapCard } from "./GapCard";
import { AnsweredFeed } from "./AnsweredFeed";

type Tab = "open" | "resolved";
type ReasonFilter = "all" | GapReason;

/**
 * Content gaps. Top-level tabs: Open / Resolved.
 *
 * Open tab also has a reason filter (all / no match / declined /
 * downvoted) so the owner can triage by failure mode — "declined" means
 * the knowledge base mentions the topic but doesn't have specific info,
 * which is the most actionable case.
 *
 * Each open cluster has two actions: Answer (writes an FAQ doc) and
 * Dismiss (just marks the cluster resolved-as-not-worth-answering).
 *
 * Deep-linkable via `?focus=<question>` — the matching card auto-opens.
 */
export function Gaps() {
  const { refresh: refreshShell } = useShell();
  const [params, setParams] = useSearchParams();
  const focusParam = params.get("focus");
  const [tab, setTab] = useState<Tab>("open");
  const [reasonFilter, setReasonFilter] = useState<ReasonFilter>("all");
  const [resolvedFilter, setResolvedFilter] = useState<ResolutionFilter>("all");

  const [data, setData]       = useState<GapsPayload | null>(null);
  const [error, setError]     = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedRefresh, setResolvedRefresh] = useState(0);

  const refresh = async () => {
    setLoading(true); setError(null);
    try {
      const d = await listGaps();
      setData(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const visibleClusters = useMemo(() => {
    const all = data?.clusters ?? [];
    if (reasonFilter === "all") return all;
    return all.filter((c) => c.primary_reason === reasonFilter || c.reasons.includes(reasonFilter));
  }, [data, reasonFilter]);

  const onResolved = (clusterId: string) => {
    // Optimistically drop the cluster from open, then refresh the home
    // payload (badge + home cards) and bump the resolved-feed refresh
    // counter so the Resolved tab shows the new entry next time it loads.
    setData((prev) => prev ? {
      ...prev,
      clusters: prev.clusters.filter((c) => c.id !== clusterId),
      total_clusters: Math.max(0, prev.total_clusters - 1),
    } : prev);
    setResolvedRefresh((n) => n + 1);
    void refreshShell();
    if (focusParam) setParams({}, { replace: true });
  };

  const breakdown = data?.reason_breakdown ?? { declined: 0, no_retrieval: 0, negative_feedback: 0 };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium text-text-strong">Content gaps</h1>
          <p className="mt-1 text-sm text-text-soft">
            Questions where the bot fell short — no match in your knowledge, declined to answer, or got a thumbs-down. Answer the ones worth teaching, dismiss the rest.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={() => void refresh()} loading={loading} iconLeft={!loading && <RotateCw size={12} strokeWidth={1.75} />}>
          Refresh
        </Button>
      </div>

      {/* Top tabs */}
      <div className="flex items-center gap-1 border-b border-line">
        <TabButton active={tab === "open"} onClick={() => setTab("open")}>
          Open
          {data && data.total_clusters > 0 && (
            <span className="ml-1.5 rounded-full bg-coral px-1.5 py-0.5 text-2xs tabular-nums text-white">
              {data.total_clusters}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === "resolved"} onClick={() => setTab("resolved")}>
          Resolved
        </TabButton>
      </div>

      {tab === "open" && (
        <>
          {error && (
            <Card className="border-danger/30">
              <div className="flex items-start gap-3 px-5 py-4">
                <AlertCircle size={18} strokeWidth={1.75} className="mt-0.5 shrink-0 text-danger" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-text-strong">Couldn't load gaps</p>
                  <p className="mt-1 text-xs text-text-soft">{error}</p>
                </div>
              </div>
            </Card>
          )}

          {!error && loading && !data && <GapsListSkeleton />}

          {!error && data && data.total_clusters === 0 && (
            <Card>
              <div className="px-5 py-6">
                <EmptyState
                  icon={Sparkles}
                  title="Your bot answered everything"
                  description="When visitors ask something your bot can't fully ground in your data, it shows up here — even when retrieval found some context but the bot still declined to answer."
                />
              </div>
            </Card>
          )}

          {!error && data && data.total_clusters > 0 && (
            <>
              {/* Reason filter pills */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 text-xs text-text-soft">
                  <FilterIcon size={12} strokeWidth={1.75} />
                  Filter
                </span>
                <ReasonPill
                  active={reasonFilter === "all"}
                  onClick={() => setReasonFilter("all")}
                  label="All"
                  count={data.total_clusters}
                />
                <ReasonPill
                  active={reasonFilter === "declined"}
                  onClick={() => setReasonFilter("declined")}
                  icon={AlertTriangle}
                  label="Declined"
                  count={breakdown.declined}
                  tone="warning"
                />
                <ReasonPill
                  active={reasonFilter === "no_retrieval"}
                  onClick={() => setReasonFilter("no_retrieval")}
                  icon={HelpCircle}
                  label="No match"
                  count={breakdown.no_retrieval}
                  tone="soft"
                />
                <ReasonPill
                  active={reasonFilter === "negative_feedback"}
                  onClick={() => setReasonFilter("negative_feedback")}
                  icon={ThumbsDown}
                  label="Downvoted"
                  count={breakdown.negative_feedback}
                  tone="danger"
                />
              </div>

              <div className="text-xs text-text-soft">
                <MessageSquareWarning size={12} strokeWidth={1.75} className="mr-1 inline align-text-bottom text-warning" />
                {visibleClusters.length === data.total_clusters
                  ? <><span className="tabular-nums">{data.total_clusters}</span> clusters covering <span className="tabular-nums">{data.total_questions}</span> visitor questions</>
                  : <>Showing <span className="tabular-nums">{visibleClusters.length}</span> of {data.total_clusters} clusters</>}
              </div>

              {visibleClusters.length === 0 ? (
                <Card>
                  <div className="px-5 py-6">
                    <EmptyState
                      icon={Sparkles}
                      title="Nothing matches this filter"
                      description="Try a different reason, or switch back to All."
                    />
                  </div>
                </Card>
              ) : (
                <div className="space-y-3">
                  {visibleClusters.map((c) => (
                    <GapCard
                      key={c.id}
                      cluster={c}
                      defaultOpen={
                        focusParam !== null &&
                        (c.canonical_question.toLowerCase() === focusParam.toLowerCase() ||
                          c.variants.some((v) => v.question.toLowerCase() === focusParam.toLowerCase()))
                      }
                      onResolved={onResolved}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === "resolved" && (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs text-text-soft">
              <FilterIcon size={12} strokeWidth={1.75} />
              Show
            </span>
            <ReasonPill active={resolvedFilter === "all"}       onClick={() => setResolvedFilter("all")}       label="All" />
            <ReasonPill active={resolvedFilter === "answered"}  onClick={() => setResolvedFilter("answered")}  label="Answered" />
            <ReasonPill active={resolvedFilter === "dismissed"} onClick={() => setResolvedFilter("dismissed")} label="Dismissed" />
          </div>
          <AnsweredFeed
            filter={resolvedFilter}
            refreshKey={resolvedRefresh}
            onChanged={() => { void refresh(); void refreshShell(); }}
          />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center border-b-2 px-4 py-2.5 text-sm transition-colors",
        active
          ? "border-coral text-text-strong"
          : "border-transparent text-text-soft hover:text-text-strong",
      )}
    >
      {children}
    </button>
  );
}

function ReasonPill({
  active,
  onClick,
  label,
  count,
  icon: Icon,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
  icon?: typeof AlertTriangle;
  tone?: "warning" | "danger" | "soft";
}) {
  const toneClass = tone === "warning" ? "text-warning"
                  : tone === "danger"  ? "text-danger"
                  : "text-text-soft";
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
        active
          ? "border-coral bg-coral-soft text-text-strong"
          : "border-line bg-surface text-text hover:border-line-2",
      )}
    >
      {Icon && <Icon size={11} strokeWidth={1.75} className={cn(active ? "text-coral" : toneClass)} />}
      {label}
      {typeof count === "number" && (
        <span className="ml-0.5 tabular-nums text-text-soft">{count}</span>
      )}
    </button>
  );
}

function GapsListSkeleton() {
  return (
    <div className="space-y-3">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="card">
          <div className="flex items-start justify-between gap-4 px-5 py-4">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-8 w-20" />
          </div>
        </div>
      ))}
    </div>
  );
}
